import { useCallback, useEffect, useRef, useState } from "react";
import {
  AnimatePresence,
  motion,
  useReducedMotion,
  useScroll,
  useTransform,
} from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import {
  ArrowRight,
  ArrowUpRight,
  BadgeCheck,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  Clock,
  Droplets,
  Gauge,
  Handshake,
  Leaf,
  Mail,
  MapPin,
  Package,
  Phone,
  Quote,
  Search,
  Send,
  ShieldCheck,
  Star,
  SunMedium,
  Waves,
  Wrench,
  X,
  Zap,
  ZoomIn,
} from "lucide-react";
import { LOGO, NAV, goTo } from "./Navbar";
import { useQuote } from "./QuoteModal";
import { HERO_SLIDES } from "./brand";
import { DEFAULT_LANDING, loadLanding } from "../Libs/siteData";
import Seo from "../seo/Seo";
import Faq from "../seo/Faq";
import { PAGE_SEO } from "../seo/pages";
import { localBusinessSchema, websiteSchema, faqSchema } from "../seo/schema";
import { FAQS } from "../seo/faqs";

// Kept so any other file that imports HERO_SLIDES from Landing keeps working.
export { HERO_SLIDES };

/* -------------------------------------------------------------------------- */
/*  CONTENT                                                                   */
/*  Editable content (hero, gallery, testimonials, contact) comes from        */
/*  Firestore (site/landing). If that fails or times out, DEFAULT_LANDING     */
/*  from siteData.js is used instead. The rest below is static.               */
/* -------------------------------------------------------------------------- */

const SLIDE_MS = 6500;
const DB_TIMEOUT_MS = 6000;

const FEATURE_SERVICES = [
  {
    title: "Borehole drilling",
    text: "From the first test hole to casing, development and a finished, flowing borehole for homes, farms and businesses.",
    img: HERO_SLIDES[0].src,
    alt: HERO_SLIDES[0].alt,
    icon: Droplets,
  },
  {
    title: "Solar installation",
    text: "Panels, inverters and batteries sized for your actual load, installed cleanly and tested before we leave.",
    img: HERO_SLIDES[2].src,
    alt: HERO_SLIDES[2].alt,
    icon: SunMedium,
  },
];

const OTHER_SERVICES = [
  {
    title: "Water surveys and siting",
    text: "We assess the land and recommend where to drill before any money goes into the ground.",
    icon: Search,
  },
  {
    title: "Pump installation",
    text: "Submersible and surface pumps matched to your borehole depth, yield and daily demand.",
    icon: Gauge,
  },
  {
    title: "Solar water pumping",
    text: "Pump water with the sun and cut fuel and grid costs for irrigation, livestock and households.",
    icon: Zap,
  },
  {
    title: "Maintenance and repairs",
    text: "Servicing, pump replacement and system checks to keep your water and power running.",
    icon: Wrench,
  },
];

const VALUES = [
  {
    title: "Reliable supply",
    text: "We design for the dry months, not just the day we hand over.",
    icon: ShieldCheck,
  },
  {
    title: "Honest workmanship",
    text: "Clear quotes, proper materials and work you can inspect.",
    icon: BadgeCheck,
  },
  {
    title: "Clean energy",
    text: "Solar first, so water and power cost less to run.",
    icon: Leaf,
  },
];

const PROCESS = [
  {
    title: "Tell us what you need",
    text: "Call, message or fill in the form. We listen, then book a site visit.",
    icon: Phone,
  },
  {
    title: "Survey and quote",
    text: "We assess the site and send a clear, itemised quote.",
    icon: ClipboardList,
  },
  {
    title: "Drill or install",
    text: "Our crew does the work, with updates until it is finished.",
    icon: Wrench,
  },
  {
    title: "Handover and support",
    text: "We test everything, show you how it works and stay on call.",
    icon: Handshake,
  },
];

// The gallery photos come from the database, so the mosaic layout is applied by
// position. The pattern fills a 4-column grid exactly every 6 photos.
const GALLERY_LAYOUT = [
  "col-span-2 row-span-2",
  "",
  "row-span-2",
  "",
  "col-span-2",
  "col-span-2",
];

const SHOP_CATEGORIES = [
  { label: "Water pumps", icon: Gauge },
  { label: "Solar panels", icon: SunMedium },
  { label: "Water tanks", icon: Waves },
  { label: "Fittings and accessories", icon: Package },
];

/* -------------------------------------------------------------------------- */
/*  DATA LOADING                                                              */
/* -------------------------------------------------------------------------- */

const withTimeout = (promise, ms) =>
  Promise.race([
    promise,
    new Promise((_, reject) => setTimeout(() => reject(new Error("timeout")), ms)),
  ]);

const isList = (v) => Array.isArray(v);
const text = (v, fallback) =>
  typeof v === "string" && v.trim() ? v : fallback;

// Makes sure a half-filled or older document can never break the page:
// anything missing or malformed falls back to the default for that field.
function normalize(data) {
  const d = DEFAULT_LANDING;
  const hero = data?.hero || {};
  const contact = data?.contact || {};

  const slides = (isList(hero.slides) ? hero.slides : []).filter((s) => s?.src);
  const gallery = isList(data?.gallery)
    ? data.gallery.filter((g) => g?.src)
    : d.gallery;
  const testimonials = isList(data?.testimonials)
    ? data.testimonials.filter((t) => t?.quote?.trim())
    : d.testimonials;

  const lat = Number(contact.lat);
  const lng = Number(contact.lng);

  return {
    hero: {
      title: text(hero.title, d.hero.title),
      text: text(hero.text, d.hero.text),
      slides: slides.length ? slides : d.hero.slides,
    },
    gallery,
    testimonials,
    contact: {
      phone: text(contact.phone, d.contact.phone),
      email: text(contact.email, d.contact.email),
      hours: text(contact.hours, d.contact.hours),
      place: text(contact.place, d.contact.place),
      lat: Number.isFinite(lat) && contact.lat !== "" ? lat : d.contact.lat,
      lng: Number.isFinite(lng) && contact.lng !== "" ? lng : d.contact.lng,
    },
  };
}

// Renders instantly with the defaults, then swaps in the live content when the
// database answers. If the database errors or takes too long, nothing changes.
function useLandingContent() {
  const [content, setContent] = useState(() => normalize(DEFAULT_LANDING));

  useEffect(() => {
    let alive = true;
    withTimeout(loadLanding(), DB_TIMEOUT_MS)
      .then(({ data }) => {
        if (alive) setContent(normalize(data));
      })
      .catch(() => {
        /* keep the default content from siteData.js */
      });
    return () => {
      alive = false;
    };
  }, []);

  return content;
}

/* -------------------------------------------------------------------------- */
/*  HELPERS                                                                   */
/* -------------------------------------------------------------------------- */

function loadLeaflet() {
  return new Promise((resolve, reject) => {
    if (window.L) return resolve(window.L);
    if (!document.getElementById("leaflet-css")) {
      const link = document.createElement("link");
      link.id = "leaflet-css";
      link.rel = "stylesheet";
      link.href =
        "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.css";
      document.head.appendChild(link);
    }
    let script = document.getElementById("leaflet-js");
    if (!script) {
      script = document.createElement("script");
      script.id = "leaflet-js";
      script.src =
        "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.js";
      script.async = true;
      document.body.appendChild(script);
    }
    script.addEventListener("load", () => resolve(window.L));
    script.addEventListener("error", reject);
  });
}

/* -------------------------------------------------------------------------- */
/*  HERO                                                                      */
/* -------------------------------------------------------------------------- */

const heroContainer = {
  hidden: {},
  show: { transition: { staggerChildren: 0.14, delayChildren: 0.15 } },
};
const heroItem = {
  hidden: { opacity: 0, y: 28 },
  show: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } },
};

function Hero({ ready, hero }) {
  const { openQuote } = useQuote();
  const reduce = useReducedMotion();
  const [active, setActive] = useState(0);
  const { scrollY } = useScroll();
  const slides = hero.slides;
  // The list can change when the live content arrives, so never trust `active` blindly.
  const current = active % slides.length;

  const vh = typeof window !== "undefined" ? window.innerHeight : 800;
  const contentOpacity = useTransform(scrollY, [0, vh * 0.55], [1, 0]);
  const contentY = useTransform(scrollY, [0, vh], [0, -90]);
  const imageY = useTransform(scrollY, [0, vh], [0, vh * 0.14]);
  const darken = useTransform(scrollY, [0, vh * 0.9], [0, 0.6]);

  useEffect(() => {
    if (!ready || slides.length < 2) return;
    const t = setTimeout(
      () => setActive((a) => (a + 1) % slides.length),
      SLIDE_MS
    );
    return () => clearTimeout(t);
  }, [active, ready, slides.length]);

  return (
    <section
      id="home"
      className="sticky top-0 z-0 h-[100svh] min-h-[640px] overflow-hidden bg-[#072445]"
    >
      {/* background slides */}
      <motion.div className="absolute inset-0" style={{ y: imageY }}>
        {slides.map((slide, i) => (
          <motion.img
            key={slide.src + i}
            src={slide.src}
            alt={i === current ? slide.alt : ""}
            aria-hidden={i !== current}
            className="absolute inset-0 h-[115%] w-full object-cover"
            initial={false}
            animate={{
              opacity: i === current ? 1 : 0,
              scale: reduce ? 1 : i === current ? 1.08 : 1,
            }}
            transition={{
              opacity: { duration: 1.5, ease: "easeInOut" },
              scale: { duration: reduce ? 0 : SLIDE_MS / 1000 + 2, ease: "linear" },
            }}
          />
        ))}
      </motion.div>

      {/* readability scrim: deep blue on the left, clear on the right */}
      <div className="hero-scrim absolute inset-0" />
      <motion.div
        className="absolute inset-0 bg-[#031326]"
        style={{ opacity: darken }}
      />

      {/* content */}
      <motion.div
        style={{ opacity: contentOpacity, y: contentY }}
        className="relative z-10 mx-auto flex h-full max-w-7xl flex-col justify-center px-5 pb-28 pt-32 sm:px-8 lg:pb-16 lg:pt-36"
      >
        <motion.div
          variants={heroContainer}
          initial="hidden"
          animate={ready ? "show" : "hidden"}
          className="max-w-2xl"
        >
          <motion.h1
            variants={heroItem}
            className="font-heading text-[2.6rem] font-extrabold leading-[1.05] tracking-tight text-white sm:text-6xl lg:text-7xl"
          >
            {hero.title}
          </motion.h1>
          <motion.p
            variants={heroItem}
            className="mt-6 max-w-xl text-lg leading-relaxed text-white/85 sm:text-xl"
          >
            {hero.text}
          </motion.p>
          <motion.div
            variants={heroItem}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              onClick={() => openQuote()}
              className="group inline-flex items-center gap-2 rounded-xl bg-[#F2A93B] px-7 py-4 text-base font-bold text-[#0B1F3A] transition-all duration-300 hover:-translate-y-0.5 hover:bg-white hover:shadow-xl"
            >
              Get a free quote
              <ArrowRight
                size={18}
                className="transition-transform duration-300 group-hover:translate-x-1"
              />
            </button>
            <button
              onClick={() => goTo("services")}
              className="group inline-flex items-center gap-2 rounded-xl border border-white/60 px-7 py-4 text-base font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-white hover:text-[#0A3568] hover:shadow-xl"
            >
              Our services
              <ArrowRight
                size={18}
                className="transition-transform duration-300 group-hover:translate-x-1"
              />
            </button>
          </motion.div>

          <motion.ul
            variants={heroItem}
            className="mt-14 flex flex-wrap gap-x-10 gap-y-4 text-white"
          >
            {[
              { icon: Droplets, label: "Borehole drilling" },
              { icon: SunMedium, label: "Solar installation" },
              { icon: Gauge, label: "Solar water pumping" },
            ].map(({ icon: Icon, label }) => (
              <li
                key={label}
                className="group flex items-center gap-3 transition-transform duration-300 hover:-translate-y-0.5"
              >
                <Icon
                  size={22}
                  className="text-[#7DB8FF] transition-colors group-hover:text-white"
                />
                <span className="text-[15px] font-semibold">{label}</span>
              </li>
            ))}
          </motion.ul>
        </motion.div>
      </motion.div>

      {/* scroll cue */}
      <div className="absolute inset-x-0 bottom-24 z-10 mx-auto flex max-w-7xl justify-end px-5 sm:px-8 lg:bottom-8">
        <button
          onClick={() => goTo("about")}
          className="hidden items-center gap-2 text-sm font-semibold text-white/80 transition hover:text-white sm:flex"
          aria-label="Scroll to content"
        >
          Scroll
          <motion.span
            animate={reduce ? {} : { y: [0, 6, 0] }}
            transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
          >
            <ChevronDown size={20} />
          </motion.span>
        </button>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  SECTIONS                                                                  */
/* -------------------------------------------------------------------------- */

function SectionHeading({ title, text, light = false, className = "" }) {
  return (
    <div className={className}>
      <h2
        className={`font-heading text-3xl font-extrabold leading-tight tracking-tight sm:text-4xl lg:text-[2.75rem] ${
          light ? "text-white" : "text-[#0E1726]"
        }`}
      >
        {title}
      </h2>
      {text && (
        <p
          className={`mt-4 max-w-xl text-lg leading-relaxed ${
            light ? "text-white/75" : "text-slate-600"
          }`}
        >
          {text}
        </p>
      )}
    </div>
  );
}

function About() {
  return (
    <section id="about" className="scroll-mt-20 px-5 py-24 sm:px-8 lg:py-32">
      <div className="mx-auto grid max-w-7xl items-center gap-16 lg:grid-cols-2">
        <div className="relative">
          <img
            src={HERO_SLIDES[1].src}
            alt={HERO_SLIDES[1].alt}
            className="aspect-[4/5] w-full object-cover sm:aspect-[5/4] lg:aspect-[4/5]"
            style={{ borderRadius: 16 }}
            loading="lazy"
          />
          <img
            src={HERO_SLIDES[2].src}
            alt={HERO_SLIDES[2].alt}
            className="absolute -bottom-10 -right-2 hidden w-[46%] border-8 border-white object-cover shadow-xl sm:block lg:-right-8"
            style={{ borderRadius: 16, aspectRatio: "1 / 1" }}
            loading="lazy"
          />
        </div>

        <div>
          <SectionHeading
            title="Borehole drilling and solar you can count on."
            text="MajiMaji Hub helps households, farms and businesses get a dependable water supply and clean energy. We survey the site, drill the borehole, and install the pumps and solar systems that keep it all running."
          />
          <ul className="mt-10 divide-y divide-slate-200 border-y border-slate-200">
            {VALUES.map(({ title, text, icon: Icon }) => (
              <li
                key={title}
                className="group flex gap-5 py-6 transition-all duration-300 hover:pl-3"
              >
                <Icon
                  size={26}
                  className="mt-1 shrink-0 text-[#1F7BE0] transition-transform duration-300 group-hover:scale-110"
                />
                <div>
                  <h3 className="font-heading text-lg font-bold text-[#0E1726]">
                    {title}
                  </h3>
                  <p className="mt-1 text-slate-600">{text}</p>
                </div>
              </li>
            ))}
          </ul>
          <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3">
            <button
              onClick={() => goTo("contact")}
              className="inline-flex items-center gap-2 font-bold text-[#0A3568] transition hover:gap-3 hover:text-[#1F7BE0]"
            >
              Talk to our team
              <ArrowRight size={18} />
            </button>
            <Link
              to="/about"
              className="inline-flex items-center gap-2 font-bold text-[#0A3568] transition hover:gap-3 hover:text-[#1F7BE0]"
            >
              How we work
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function Services() {
  const { openQuote } = useQuote();
  return (
    <section id="services" className="scroll-mt-20 bg-[#F3F7FB] px-5 py-24 sm:px-8 lg:py-32">
      <div className="mx-auto max-w-7xl">
        <div className="flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
          <SectionHeading
            title="Borehole drilling, pumps and solar installation"
            text="Two things, done properly: getting water out of the ground and powering your property from the sun."
          />
          <button
            className="inline-flex items-center gap-2 self-start font-bold text-[#0A3568] transition hover:gap-3 hover:text-[#1F7BE0] lg:self-auto"
            onClick={() => openQuote()}
          >
            Request a quote
            <ArrowRight size={18} />
          </button>
        </div>

        {/* two large feature panels */}
        <div className="mt-14 grid gap-6 md:grid-cols-2">
          {FEATURE_SERVICES.map(({ title, text, img, alt, icon: Icon }) => (
            <article
              key={title}
              className="group relative isolate flex min-h-[420px] flex-col justify-end overflow-hidden text-white"
              style={{ borderRadius: 16 }}
            >
              <img
                src={img}
                alt={alt}
                loading="lazy"
                className="absolute inset-0 -z-10 h-full w-full object-cover transition-transform duration-[1200ms] group-hover:scale-105"
              />
              <div className="absolute inset-0 -z-10 bg-gradient-to-t from-[#072445] via-[#072445]/55 to-transparent" />
              <span className="absolute right-6 top-6 flex h-11 w-11 translate-y-2 items-center justify-center rounded-xl bg-white/90 text-[#0A3568] opacity-0 transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100">
                <ArrowUpRight size={20} />
              </span>
              <div className="p-8">
                <Icon size={30} className="text-[#7DB8FF]" />
                <h3 className="font-heading mt-4 text-2xl font-extrabold">
                  {title}
                </h3>
                <p className="mt-2 max-w-md text-white/80">{text}</p>
              </div>
            </article>
          ))}
        </div>

        {/* the rest as a ruled list */}
        <ul className="mt-6 grid divide-slate-300 border-t border-slate-300 md:grid-cols-2 md:divide-x lg:grid-cols-4">
          {OTHER_SERVICES.map(({ title, text, icon: Icon }) => (
            <li
              key={title}
              className="group border-b border-slate-300 py-8 transition-colors duration-300 hover:bg-white md:px-8 md:first:pl-0 lg:border-b-0 lg:last:pr-0"
            >
              <Icon
                size={26}
                className="text-[#1F7BE0] transition-all duration-300 group-hover:-translate-y-1 group-hover:text-[#0A3568]"
              />
              <h3 className="font-heading mt-4 text-lg font-bold text-[#0E1726]">
                {title}
              </h3>
              <p className="mt-2 text-[15px] leading-relaxed text-slate-600">
                {text}
              </p>
            </li>
          ))}
        </ul>

        <div className="mt-10">
          <Link
            to="/services"
            className="inline-flex items-center gap-2 font-bold text-[#0A3568] transition hover:gap-3 hover:text-[#1F7BE0]"
          >
            See all services and what is included
            <ArrowRight size={18} />
          </Link>
        </div>
      </div>
    </section>
  );
}

function Process() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start 80%", "end 60%"],
  });
  const lineScale = useTransform(scrollYProgress, [0, 1], [0, 1]);

  return (
    <section
      ref={ref}
      className="relative overflow-hidden bg-gradient-to-b from-[#0A3568] to-[#072445] px-5 py-24 text-white sm:px-8 lg:py-32"
    >
      <div className="mx-auto max-w-7xl">
        <SectionHeading
          light
          title="How a project runs"
          text="A simple path from the first call to a working system."
        />

        <div className="relative mt-16">
          {/* the line draws as you scroll */}
          <div className="absolute left-0 right-0 top-[27px] hidden h-px bg-white/15 lg:block" />
          <motion.div
            className="absolute left-0 right-0 top-[27px] hidden h-px origin-left bg-[#7DB8FF] lg:block"
            style={{ scaleX: lineScale }}
          />
          <ol className="grid gap-10 lg:grid-cols-4 lg:gap-8">
            {PROCESS.map(({ title, text, icon: Icon }, i) => (
              <li key={title} className="group relative">
                <div className="relative z-10 flex h-14 w-14 items-center justify-center border border-white/25 bg-[#0A3568] transition-all duration-300 group-hover:-translate-y-1 group-hover:border-[#7DB8FF] group-hover:bg-[#1F7BE0]" style={{ borderRadius: 12 }}>
                  <Icon size={24} className="text-[#7DB8FF]" />
                </div>
                <p className="font-heading mt-6 text-sm font-bold text-[#7DB8FF]">
                  Step {i + 1}
                </p>
                <h3 className="font-heading mt-1 text-xl font-bold">{title}</h3>
                <p className="mt-2 max-w-xs leading-relaxed text-white/70">
                  {text}
                </p>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
}

function Gallery({ items }) {
  const [open, setOpen] = useState(null);

  useEffect(() => {
    if (open === null) return;
    const onKey = (e) => e.key === "Escape" && setOpen(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  // If the list shrinks while a photo is open, close the viewer.
  useEffect(() => {
    if (open !== null && !items[open]) setOpen(null);
  }, [items, open]);

  if (!items.length) return null;

  return (
    <section id="gallery" className="scroll-mt-20 px-5 py-24 sm:px-8 lg:py-32">
      <div className="mx-auto max-w-7xl">
        <SectionHeading
          title="Borehole and solar projects we have completed"
          text="A look at recent drilling and solar projects. Tap any photo to enlarge it."
        />
        <div className="mt-14 grid auto-rows-[150px] grid-flow-dense grid-cols-2 gap-3 sm:auto-rows-[200px] md:grid-cols-4 md:gap-4">
          {items.map((g, i) => (
            <button
              key={g.src + i}
              onClick={() => setOpen(i)}
              className={`group relative overflow-hidden ${
                GALLERY_LAYOUT[i % GALLERY_LAYOUT.length]
              }`}
              style={{ borderRadius: 12 }}
              aria-label={`Open photo: ${g.alt}`}
            >
              <img
                src={g.src}
                alt={g.alt}
                loading="lazy"
                className="h-full w-full object-cover transition-transform duration-[900ms] group-hover:scale-105"
              />
              <span className="absolute inset-0 flex items-center justify-center bg-[#072445]/0 transition-colors duration-300 group-hover:bg-[#072445]/35">
                <span className="flex h-12 w-12 scale-75 items-center justify-center rounded-xl bg-white text-[#0A3568] opacity-0 transition-all duration-300 group-hover:scale-100 group-hover:opacity-100">
                  <ZoomIn size={22} />
                </span>
              </span>
            </button>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {open !== null && items[open] && (
          <motion.div
            className="fixed inset-0 z-[80] flex items-center justify-center bg-[#031326]/90 p-5"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setOpen(null)}
            role="dialog"
            aria-modal="true"
            aria-label="Photo viewer"
          >
            <button
              className="absolute right-5 top-5 text-white"
              onClick={() => setOpen(null)}
              aria-label="Close photo"
            >
              <X size={32} />
            </button>
            <motion.img
              src={items[open].src}
              alt={items[open].alt}
              className="max-h-[85vh] max-w-full object-contain"
              initial={{ scale: 0.94, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.94, opacity: 0 }}
              transition={{ duration: 0.3 }}
              onClick={(e) => e.stopPropagation()}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}

function Shop() {
  return (
    <section id="shop" className="scroll-mt-20 bg-[#F3F7FB] px-5 py-24 sm:px-8 lg:py-28">
      <div className="mx-auto grid max-w-7xl items-center gap-12 lg:grid-cols-[1fr_1.2fr]">
        <div>
          <SectionHeading
            title="Buy pumps, panels and tanks"
            text="Can you do the install yourself, or just need the right equipment? Browse what we stock."
          />
          <Link
            to="/shop"
            className="group mt-8 inline-flex items-center gap-2 bg-[#0A3568] px-7 py-4 font-bold text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#1F7BE0] hover:shadow-xl"
            style={{ borderRadius: 12 }}
          >
            Visit the shop
            <ArrowUpRight
              size={18}
              className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
            />
          </Link>
        </div>
        <ul className="grid grid-cols-2 gap-4">
          {SHOP_CATEGORIES.map(({ label, icon: Icon }) => (
            <li key={label}>
              <button
                className="group flex h-full w-full flex-col items-start justify-between gap-10 border border-slate-300 bg-white p-6 text-left transition-all duration-300 hover:-translate-y-1 hover:border-[#0A3568] hover:bg-[#0A3568] hover:shadow-xl"
                style={{ borderRadius: 12 }}
              >
                <Icon
                  size={30}
                  className="text-[#1F7BE0] transition group-hover:text-[#7DB8FF]"
                />
                <span className="font-heading text-lg font-bold text-[#0E1726] transition group-hover:text-white">
                  {label}
                </span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function Testimonials({ items }) {
  const [i, setI] = useState(0);
  const count = items.length;
  const index = count ? i % count : 0;

  const next = useCallback(
    () => setI((v) => (count ? (v + 1) % count : 0)),
    [count]
  );
  const prev = () => setI((v) => (count ? (v - 1 + count) % count : 0));

  useEffect(() => {
    if (count < 2) return;
    const t = setTimeout(next, 9000);
    return () => clearTimeout(t);
  }, [index, next, count]);

  if (!count) return null;
  const t = items[index];

  return (
    <section className="px-5 py-24 sm:px-8 lg:py-32">
      <div className="mx-auto max-w-4xl">
        <Quote size={44} className="text-[#1F7BE0]" />
        <div className="relative mt-6 min-h-[260px] sm:min-h-[220px]">
          <AnimatePresence mode="wait">
            <motion.figure
              key={index}
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -14 }}
              transition={{ duration: 0.45 }}
            >
              <blockquote className="font-heading text-2xl font-bold leading-snug text-[#0E1726] sm:text-3xl">
                {t.quote}
              </blockquote>
              <figcaption className="mt-7 flex items-center gap-4">
                <div className="flex gap-0.5" aria-label="5 out of 5 stars">
                  {[...Array(5)].map((_, s) => (
                    <Star
                      key={s}
                      size={18}
                      className="fill-[#F2A93B] text-[#F2A93B]"
                    />
                  ))}
                </div>
                <span className="font-semibold text-slate-700">
                  {[t.name, t.place].filter(Boolean).join(", ")}
                </span>
              </figcaption>
            </motion.figure>
          </AnimatePresence>
        </div>

        {count > 1 && (
          <div className="mt-10 flex items-center gap-3">
            <button
              onClick={prev}
              aria-label="Previous testimonial"
              className="flex h-12 w-12 items-center justify-center border border-slate-300 text-[#0A3568] transition hover:bg-[#0A3568] hover:text-white"
              style={{ borderRadius: 12 }}
            >
              <ChevronLeft size={22} />
            </button>
            <button
              onClick={next}
              aria-label="Next testimonial"
              className="flex h-12 w-12 items-center justify-center border border-slate-300 text-[#0A3568] transition hover:bg-[#0A3568] hover:text-white"
              style={{ borderRadius: 12 }}
            >
              <ChevronRight size={22} />
            </button>
            <span className="ml-3 text-sm font-semibold text-slate-500">
              {index + 1} / {count}
            </span>
          </div>
        )}
      </div>
    </section>
  );
}

function LocationMap({ lat, lng, place }) {
  const ref = useRef(null);

  // Re-runs when the live coordinates replace the defaults.
  useEffect(() => {
    let map;
    let cancelled = false;
    const pin = [lat, lng];

    loadLeaflet()
      .then((L) => {
        if (cancelled || !ref.current) return;
        map = L.map(ref.current, { scrollWheelZoom: false }).setView(pin, 13);
        L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
          maxZoom: 19,
          attribution: "&copy; OpenStreetMap contributors",
        }).addTo(map);
        const icon = L.divIcon({
          className: "maji-pin",
          html: '<svg width="40" height="48" viewBox="0 0 40 48" xmlns="http://www.w3.org/2000/svg"><path d="M20 46C20 46 36 30 36 18a16 16 0 0 0-32 0c0 12 16 28 16 28z" fill="#0A3568"/><circle cx="20" cy="18" r="6.5" fill="#fff"/></svg>',
          iconSize: [40, 48],
          iconAnchor: [20, 46],
          popupAnchor: [0, -40],
        });

        // Built as DOM nodes (not an HTML string) because the place name is admin-entered.
        const popup = document.createElement("div");
        const name = document.createElement("strong");
        name.textContent = "MajiMaji Hub";
        popup.appendChild(name);
        popup.appendChild(document.createElement("br"));
        popup.appendChild(document.createTextNode(place));

        L.marker(pin, { icon }).addTo(map).bindPopup(popup);
      })
      .catch(() => {});

    return () => {
      cancelled = true;
      if (map) map.remove();
    };
  }, [lat, lng, place]);

  return (
    <div
      ref={ref}
      className="h-full min-h-[360px] w-full"
      role="region"
      aria-label={`Map showing the MajiMaji office in ${place}`}
    />
  );
}

function Contact({ contact }) {
  const { openQuote } = useQuote();
  const [sent, setSent] = useState(false);

  const onSubmit = (e) => {
    e.preventDefault();
    // TODO: connect to your backend (Firestore messages collection, Cloud Function, WhatsApp, email).
    setSent(true);
  };

  const field =
    "w-full border border-slate-300 bg-white px-4 py-3.5 text-[#0E1726] outline-none transition focus:border-[#1F7BE0] focus:ring-2 focus:ring-[#1F7BE0]/25";

  return (
    <section
      id="contact"
      className="scroll-mt-20 bg-gradient-to-b from-[#0A3568] to-[#072445] px-5 py-24 sm:px-8 lg:py-32"
    >
      <div className="mx-auto max-w-7xl">
        <SectionHeading
          light
          title="Send us a message"
          text="A question, some feedback or a quick enquiry? Write to us and we will reply within one working day."
        />
        <p className="mt-5 text-white/75">
          Looking for a price?{" "}
          <button
            type="button"
            onClick={() => openQuote()}
            className="font-bold text-[#F2A93B] underline-offset-4 transition hover:text-white hover:underline"
          >
            Request a quote
          </button>
        </p>

        <div className="mt-14 grid gap-10 lg:grid-cols-2">
          <div className="space-y-8">
            <form
              onSubmit={onSubmit}
              className="bg-white p-6 sm:p-8"
              style={{ borderRadius: 16 }}
            >
              {sent ? (
                <div className="flex min-h-[340px] flex-col items-center justify-center text-center">
                  <BadgeCheck size={48} className="text-[#1F7BE0]" />
                  <h3 className="font-heading mt-4 text-2xl font-extrabold text-[#0E1726]">
                    Message sent
                  </h3>
                  <p className="mt-2 max-w-sm text-slate-600">
                    Thank you. We will get back to you within one working day.
                  </p>
                </div>
              ) : (
                <div className="grid gap-5 sm:grid-cols-2">
                  <label className="block">
                    <span className="mb-1.5 block text-sm font-semibold text-slate-700">
                      Your name
                    </span>
                    <input required name="name" className={field} />
                  </label>
                  <label className="block">
                    <span className="mb-1.5 block text-sm font-semibold text-slate-700">
                      Phone number
                    </span>
                    <input required type="tel" name="phone" className={field} />
                  </label>
                  <label className="block sm:col-span-2">
                    <span className="mb-1.5 flex items-baseline justify-between text-sm font-semibold text-slate-700">
                      Email
                      <span className="font-normal text-slate-400">Optional</span>
                    </span>
                    <input type="email" name="email" className={field} />
                  </label>
                  <label className="block sm:col-span-2">
                    <span className="mb-1.5 block text-sm font-semibold text-slate-700">
                      Message
                    </span>
                    <textarea
                      required
                      name="message"
                      rows={4}
                      className={field}
                      placeholder="How can we help?"
                    />
                  </label>
                  <button
                    type="submit"
                    className="inline-flex items-center justify-center gap-2 bg-[#F2A93B] px-7 py-4 font-bold text-[#0B1F3A] transition hover:bg-[#0A3568] hover:text-white sm:col-span-2"
                    style={{ borderRadius: 12 }}
                  >
                    <Send size={18} />
                    Send message
                  </button>
                </div>
              )}
            </form>

            <ul className="grid gap-5 text-white sm:grid-cols-2">
              {[
                { icon: Phone, label: "Call us", value: contact.phone, href: `tel:${contact.phone.replace(/[^\d+]/g, "")}` },
                { icon: Mail, label: "Email", value: contact.email, href: `mailto:${contact.email}` },
                { icon: MapPin, label: "Location", value: contact.place },
                { icon: Clock, label: "Hours", value: contact.hours },
              ].map(({ icon: Icon, label, value, href }) => (
                <li key={label} className="flex gap-4">
                  <Icon size={22} className="mt-0.5 shrink-0 text-[#7DB8FF]" />
                  <div>
                    <p className="text-sm text-white/60">{label}</p>
                    {href ? (
                      <a href={href} className="font-semibold hover:underline">{value}</a>
                    ) : (
                      <p className="font-semibold">{value}</p>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <div
            className="relative z-0 min-h-[420px] overflow-hidden lg:min-h-full"
            style={{ borderRadius: 16 }}
          >
            <LocationMap
              lat={contact.lat}
              lng={contact.lng}
              place={contact.place}
            />
          </div>
        </div>
      </div>
    </section>
  );
}

function Footer({ contact }) {
  return (
    <footer className="bg-[#041A33] px-5 pb-32 pt-14 text-white sm:px-8 lg:pb-14">
      <div className="mx-auto grid max-w-7xl gap-10 md:grid-cols-[1.4fr_1fr_1fr]">
        <div>
          <img
            src={LOGO}
            alt="MajiMaji Hub"
            className="h-14 w-auto brightness-0 invert"
          />
          <p className="mt-5 max-w-sm leading-relaxed text-white/65">
            Borehole drilling and solar installation for homes, farms and
            businesses across Kenya.
          </p>
        </div>
        <div>
          <h4 className="font-heading font-bold">Explore</h4>
          <ul className="mt-4 space-y-3 text-white/70">
            {NAV.map((n) => (
              <li key={n.href}>
                <Link
                  to={n.href}
                  className="link-underline transition-colors hover:text-white"
                >
                  {n.label}
                </Link>
              </li>
            ))}
            <li>
              <button
                onClick={() => goTo("contact")}
                className="link-underline transition-colors hover:text-white"
              >
                Contact us
              </button>
            </li>
          </ul>
        </div>
        <div>
          <h4 className="font-heading font-bold">Contact</h4>
          <ul className="mt-4 space-y-3 text-white/70">
            <li>{contact.phone}</li>
            <li>{contact.email}</li>
            <li>{contact.place}</li>
          </ul>
        </div>
      </div>
      <div className="mx-auto mt-12 max-w-7xl border-t border-white/10 pt-6 text-sm text-white/50">
        &copy; {new Date().getFullYear()} MajiMaji Hub. All rights
        reserved.
      </div>
    </footer>
  );
}

/* -------------------------------------------------------------------------- */
/*  PAGE                                                                      */
/*  The navbar and the loader live in App.js and cover every route.           */
/*  `ready` turns true once the loader has finished, which starts the hero    */
/*  animation.                                                                */
/* -------------------------------------------------------------------------- */

export default function Landing({ ready = true }) {
  const location = useLocation();
  const content = useLandingContent();

  // e.g. "Contact us" pressed on /shop: App navigates here, then we scroll down
  useEffect(() => {
    const target = location.state?.scrollTo;
    if (!ready || !target) return;
    const t = setTimeout(() => {
      goTo(target);
      window.history.replaceState({}, document.title);
    }, 150);
    return () => clearTimeout(t);
  }, [ready, location.state]);

  return (
    <div className="bg-white text-[#0E1726]">
      <Seo
        {...PAGE_SEO.home}
        path="/"
        schema={[localBusinessSchema, websiteSchema, faqSchema(FAQS)]}
      />
      <main>
        <Hero ready={ready} hero={content.hero} />

        {/* This sheet slides over the pinned hero */}
        <div
          className="relative z-10 bg-white"
          style={{
            borderTopLeftRadius: 36,
            borderTopRightRadius: 36,
            boxShadow: "0 -30px 60px -20px rgba(4,20,45,0.45)",
          }}
        >
          <div
            className="overflow-hidden"
            style={{ borderTopLeftRadius: 36, borderTopRightRadius: 36 }}
          >
            <About />
            <Services />
            <Process />
            <Gallery items={content.gallery} />
            <Shop />
            <Testimonials items={content.testimonials} />
            <Faq />
            <Contact contact={content.contact} />
            <Footer contact={content.contact} />
          </div>
        </div>
      </main>
    </div>
  );
}